import { useState, useMemo } from "react";
import { useStats, usePins, useIndicacoes } from "../hooks/useIndicacoes";
import { useFilters } from "../hooks/useFilters";
import { canonicalizeBairro, areSameNeighborhood } from "../constants/neighborhoods";
import {
  THEMATIC_CATEGORIES,
  THEMATIC_CATEGORIES_ORDER,
  getCategoryStyle,
  getCategoryLabel,
} from "../constants/categories";

const CANONICAL_COUNCILORS = [
  "Alex Andrade",
  "Bianca Barros",
  "Caio Campos",
  "Diana Dias",
  "Eduardo Esteves",
  "Fernanda Freitas",
  "Gabriel Gomes",
  "Helena Martins",
  "Igor Nascimento",
  "Julia Oliveira",
  "Leandro Prado",
  "Marina Ribeiro",
  "Nicolas Rocha",
  "Patricia Silva",
  "Rafael Teixeira",
];

/**
 * Gráfico de Área vetorial em SVG para distribuição cronológica anual (2021-2024).
 * Apresenta a evolução com nós interativos de filtragem anual.
 */
function DistributionAreaChart({ yearData = [], selectedYears = [], onSelectYear }) {
  if (!yearData || yearData.length === 0) return null;

  const width = 280;
  const height = 80;
  const paddingX = 26;
  const paddingTop = 20;
  const paddingBottom = 22;
  const baselineY = height - paddingBottom;
  const plotHeight = baselineY - paddingTop;

  const maxVal = Math.max(...yearData.map((d) => d.count), 1);
  const stepX = (width - paddingX * 2) / Math.max(1, yearData.length - 1);

  const points = yearData.map((d, i) => {
    const x = paddingX + i * stepX;
    const y = baselineY - (d.count / maxVal) * plotHeight;
    return { ...d, x, y };
  });

  let linePath = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    linePath += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }

  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${baselineY} L ${points[0].x.toFixed(1)} ${baselineY} Z`;

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
          fontSize: "var(--text-micro)",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "var(--tracking-wide)",
          color: "var(--text-muted)",
        }}
      >
        <span>Distribuição por Ano</span>
        <span style={{ fontWeight: 600, color: "var(--text-secondary)", fontVariantNumeric: "tabular-nums" }}>
          {yearData[0]?.year} – {yearData[yearData.length - 1]?.year}
        </span>
      </div>

      <div
        style={{
          background: "transparent",
          padding: "2px 0 0",
          userSelect: "none",
        }}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: "100%", height: "auto", overflow: "visible" }}
        >
          <defs>
            <linearGradient id="distribAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.30" />
              <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Linha guia de base */}
          <line
            x1={paddingX - 10}
            y1={baselineY}
            x2={width - paddingX + 10}
            y2={baselineY}
            stroke="var(--border-subtle)"
            strokeWidth="1"
            strokeDasharray="3 3"
          />

          {/* Área preenchida com gradiente */}
          <path d={areaPath} fill="url(#distribAreaGrad)" />

          {/* Linha de contorno */}
          <path
            d={linePath}
            fill="none"
            stroke="var(--accent-primary)"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Pontos de dados, rótulos e anos */}
          {points.map((pt) => {
            const isSelected = selectedYears.includes(pt.year);

            return (
              <g
                key={pt.year}
                onClick={() => onSelectYear && onSelectYear(pt.year)}
                style={{ cursor: "pointer" }}
              >
                {/* Halo em hover/seleção */}
                {isSelected && (
                  <circle
                    cx={pt.x}
                    cy={pt.y}
                    r="8.5"
                    fill="var(--accent-light)"
                    stroke="var(--accent-primary)"
                    strokeWidth="1"
                    opacity="0.8"
                  />
                )}

                {/* Ponto de dado */}
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r={isSelected ? 4.5 : 3.5}
                  fill={isSelected ? "var(--accent-primary)" : "var(--bg-surface)"}
                  stroke="var(--accent-primary)"
                  strokeWidth={isSelected ? 2.5 : 2}
                  style={{ transition: "all var(--transition-fast)" }}
                />

                {/* Rótulo numérico acima do ponto */}
                <text
                  x={pt.x}
                  y={pt.y - 7}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="700"
                  fill={isSelected ? "var(--accent-primary)" : "var(--text-primary)"}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {pt.count}
                </text>

                {/* Rótulo do ano abaixo do eixo */}
                <text
                  x={pt.x}
                  y={height - 6}
                  textAnchor="middle"
                  fontSize="10.5"
                  fontWeight={isSelected ? "800" : "500"}
                  fill={isSelected ? "var(--accent-primary)" : "var(--text-muted)"}
                >
                  {pt.year}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

/**
 * Lista de Barras de Distribuição Temática com Layout de 2 Linhas (Anti-card).
 */
function CategoryBarsList({ categories = [], activeCategory = null, onSelectCategory }) {
  const [showAll, setShowAll] = useState(false);
  const displayedCategories = showAll ? categories : categories.slice(0, 6);

  if (categories.length === 0) {
    return (
      <div style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)", padding: "4px 0" }}>
        Nenhum tema registrado neste recorte.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {displayedCategories.map((thm) => {
        const isCatActive = activeCategory === thm.key;
        return (
          <div
            key={thm.key}
            onClick={() => onSelectCategory && onSelectCategory(isCatActive ? null : thm.key)}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 5,
              padding: "5px 6px",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              background: isCatActive ? "var(--accent-light)" : "transparent",
              transition: "background var(--transition-fast)",
            }}
            onMouseEnter={(e) => {
              if (!isCatActive) e.currentTarget.style.background = "var(--bg-surface-elevated)";
            }}
            onMouseLeave={(e) => {
              if (!isCatActive) e.currentTarget.style.background = "transparent";
            }}
            title={
              isCatActive
                ? `Remover filtro de ${thm.label || thm.name}`
                : `Filtrar indicações por ${thm.label || thm.name}`
            }
          >
            {/* Linha 1: Nome da Categoria à esquerda | Quantidade e % à direita */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span
                style={{
                  fontSize: "var(--text-label)",
                  fontWeight: isCatActive ? 700 : 600,
                  color: isCatActive ? "var(--accent-primary)" : "var(--text-primary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {thm.label || thm.name}
              </span>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, flexShrink: 0 }}>
                <span
                  style={{
                    fontSize: "var(--text-label)",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    color: "var(--text-primary)",
                  }}
                >
                  {thm.count}
                </span>
                <span
                  style={{
                    fontSize: "var(--text-micro)",
                    color: "var(--text-muted)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ({thm.percentOfTotal || Math.round(thm.percentage)}%)
                </span>
              </div>
            </div>

            {/* Linha 2: Ícone temático + Barra de distribuição proporcional */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "var(--radius-sm)",
                  background: thm.color || thm.style?.bgColor || "var(--accent-primary)",
                  color: thm.onSolid || thm.style?.onSolid || "#ffffff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                }}
              >
                <span
                  className="geom-svg-icon"
                  style={{ width: 11, height: 11 }}
                  dangerouslySetInnerHTML={{ __html: thm.icon || thm.style?.icon || "" }}
                />
              </div>

              <div
                style={{
                  flex: 1,
                  height: 6,
                  background: "var(--border-subtle)",
                  borderRadius: "var(--radius-full)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: thm.count === 0 ? "0%" : `${Math.max(2, thm.percentage || thm.barWidth)}%`,
                    height: "100%",
                    background: thm.color || thm.style?.bgColor || "var(--accent-primary)",
                    borderRadius: "var(--radius-full)",
                    transition: "width var(--transition-normal)",
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}

      {categories.length > 6 && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          style={{
            marginTop: 2,
            padding: "5px 8px",
            background: "transparent",
            border: "1px dashed var(--border-color)",
            borderRadius: "var(--radius-sm)",
            color: "var(--accent-primary)",
            fontSize: "var(--text-micro)",
            fontWeight: 650,
            cursor: "pointer",
            textAlign: "center",
            transition: "all var(--transition-fast)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-light)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {showAll
            ? "− Mostrar apenas 6 temas"
            : `+ Ver todos os ${categories.length} temas`}
        </button>
      )}
    </div>
  );
}

/**
 * Lista de Barras de Distribuição por Vereador com Layout de 2 Linhas (Anti-card).
 * Apresentação puramente descritiva (sem rankings nem avaliações de desempenho).
 */
function CouncilorBarsList({ authors = [], activeAuthor = null, onSelectAuthor }) {
  const [showAll, setShowAll] = useState(false);
  const displayedAuthors = showAll ? authors : authors.slice(0, 5);

  if (authors.length === 0) {
    return (
      <div style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)", padding: "4px 0" }}>
        Nenhum vereador registrado neste recorte.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {displayedAuthors.map((item) => {
        const isAuthorActive = activeAuthor === item.author;
        return (
          <div
            key={item.author}
            onClick={() => onSelectAuthor && onSelectAuthor(isAuthorActive ? null : item.author)}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 5,
              padding: "5px 6px",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              background: isAuthorActive ? "var(--accent-light)" : "transparent",
              transition: "background var(--transition-fast)",
            }}
            onMouseEnter={(e) => {
              if (!isAuthorActive) e.currentTarget.style.background = "var(--bg-surface-elevated)";
            }}
            onMouseLeave={(e) => {
              if (!isAuthorActive) e.currentTarget.style.background = "transparent";
            }}
            title={
              isAuthorActive
                ? `Remover filtro de ${item.author}`
                : `Filtrar indicações de ${item.author}`
            }
          >
            {/* Linha 1: Nome do Vereador | Quantidade e % */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span
                style={{
                  fontSize: "var(--text-label)",
                  fontWeight: isAuthorActive ? 700 : 600,
                  color: isAuthorActive ? "var(--accent-primary)" : "var(--text-primary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.author}
              </span>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, flexShrink: 0 }}>
                <span
                  style={{
                    fontSize: "var(--text-label)",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    color: "var(--text-primary)",
                  }}
                >
                  {item.count}
                </span>
                <span
                  style={{
                    fontSize: "var(--text-micro)",
                    color: "var(--text-muted)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ({item.percentOfTotal || Math.round(item.percentage)}%)
                </span>
              </div>
            </div>

            {/* Linha 2: Ícone do Vereador + Barra de distribuição proporcional */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "var(--radius-sm)",
                  background: isAuthorActive ? "var(--accent-primary)" : "var(--accent-light)",
                  color: isAuthorActive ? "#ffffff" : "var(--accent-primary)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>

              <div
                style={{
                  flex: 1,
                  height: 6,
                  background: "var(--border-subtle)",
                  borderRadius: "var(--radius-full)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: item.count === 0 ? "0%" : `${Math.max(2, item.percentage)}%`,
                    height: "100%",
                    background: "var(--accent-primary)",
                    borderRadius: "var(--radius-full)",
                    transition: "width var(--transition-normal)",
                    opacity: isAuthorActive ? 1 : 0.85,
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}

      {authors.length > 5 && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          style={{
            marginTop: 2,
            padding: "5px 8px",
            background: "transparent",
            border: "1px dashed var(--border-color)",
            borderRadius: "var(--radius-sm)",
            color: "var(--accent-primary)",
            fontSize: "var(--text-micro)",
            fontWeight: 650,
            cursor: "pointer",
            textAlign: "center",
            transition: "all var(--transition-fast)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-light)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {showAll
            ? "− Mostrar apenas 5 vereadores"
            : `+ Ver todos os ${authors.length} vereadores`}
        </button>
      )}
    </div>
  );
}

/**
 * Modal informativo elegante que serve de ponte para o futuro módulo completo de estatísticas.
 */
function AnalyticsModal({ isOpen, onClose, summary }) {
  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-card"
        style={{
          width: "100%",
          maxWidth: 480,
          background: "var(--bg-surface)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-glass)",
          border: "1px solid var(--border-color)",
          padding: 24,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* Cabeçalho do Modal */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span
                style={{
                  fontSize: "var(--text-micro)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  padding: "2px 8px",
                  borderRadius: "var(--radius-full)",
                  background: "var(--accent-light)",
                  color: "var(--accent-primary)",
                  border: "1px solid rgba(37, 99, 235, 0.2)",
                }}
              >
                Módulo em Expansão
              </span>
            </div>
            <h3 style={{ margin: 0, fontSize: "var(--text-title)", fontWeight: 700, color: "var(--text-primary)" }}>
              Relatório Analítico Completo
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-muted)",
              cursor: "pointer",
              padding: 4,
              borderRadius: "var(--radius-sm)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Resumo Instantâneo em Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}
        >
          <div
            style={{
              background: "var(--bg-app)",
              padding: "10px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-color)",
            }}
          >
            <div style={{ fontSize: "var(--text-label)", color: "var(--text-muted)", fontWeight: 600 }}>Total de Indicações</div>
            <div style={{ fontSize: "var(--text-title)", fontWeight: 800, color: "var(--text-primary)", marginTop: 2 }}>
              {summary.total}
            </div>
          </div>
          <div
            style={{
              background: "var(--bg-app)",
              padding: "10px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-color)",
            }}
          >
            <div style={{ fontSize: "var(--text-label)", color: "var(--text-muted)", fontWeight: 600 }}>Vereadores Atuantes</div>
            <div style={{ fontSize: "var(--text-title)", fontWeight: 800, color: "var(--accent-primary)", marginTop: 2 }}>
              {summary.totalAuthors}
            </div>
          </div>
          <div
            style={{
              background: "var(--bg-app)",
              padding: "10px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-color)",
            }}
          >
            <div style={{ fontSize: "var(--text-label)", color: "var(--text-muted)", fontWeight: 600 }}>Categoria Líder</div>
            <div style={{ fontSize: "var(--text-body-sm)", fontWeight: 700, color: "var(--text-primary)", marginTop: 4 }}>
              {summary.topCategory}
            </div>
          </div>
          <div
            style={{
              background: "var(--bg-app)",
              padding: "10px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-color)",
            }}
          >
            <div style={{ fontSize: "var(--text-label)", color: "var(--text-muted)", fontWeight: 600 }}>Mandato com Maior Volume</div>
            <div
              style={{
                fontSize: "var(--text-body-sm)",
                fontWeight: 700,
                color: "var(--text-primary)",
                marginTop: 4,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={summary.topAuthor}
            >
              {summary.topAuthor}
            </div>
          </div>
        </div>

        {/* Mensagem Institucional */}
        <p style={{ margin: 0, fontSize: "var(--text-body-sm)", color: "var(--text-secondary)", lineHeight: "var(--leading-normal)" }}>
          A visualização analítica expandida (com cruzamentos por bairros, correlações de temas por mandato e ferramentas de exportação em CSV/PDF) está sendo desenhada na página dedicada de transparência.
        </p>

        {/* Botão de Fechar */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "var(--accent-primary)",
              color: "#ffffff",
              border: "none",
              borderRadius: "var(--radius-md)",
              padding: "8px 18px",
              fontSize: "var(--text-label)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Painel de estatísticas interativo à direita do dashboard.
 */
export default function StatsPanel({
  isCollapsed,
  onToggleCollapse,
  isMobile,
  topOffset = 68,
  navigate,
}) {
  const { stats } = useStats();
  const { pins } = usePins();
  const { total: indicacoesTotal } = useIndicacoes();
  const {
    filters,
    applyFilters,
    setAno,
    setAutor,
    setThematicCategory,
    setBairro,
    setSearch,
    setAproximado,
    resetFilters,
    hasActiveFilters,
  } = useFilters();

  const [showAllAuthors, setShowAllAuthors] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const safePins = pins || [];
  const safeStats = stats || {};

  const availableAuthors = useMemo(() => {
    if (Array.isArray(safeStats.autores) && safeStats.autores.length > 0) {
      return [...safeStats.autores].sort((a, b) => a.localeCompare(b, "pt-BR"));
    }
    return CANONICAL_COUNCILORS;
  }, [safeStats.autores]);

  // Anos disponíveis extraídos dos dados
  const availableYears = useMemo(() => {
    const yearSet = new Set();
    if (Array.isArray(safeStats.anos)) {
      safeStats.anos.forEach((y) => {
        if (Number(y)) yearSet.add(Number(y));
      });
    }
    if (Array.isArray(safeStats.demandas_por_ano)) {
      safeStats.demandas_por_ano.forEach((item) => {
        if (Number(item?.ano)) yearSet.add(Number(item.ano));
      });
    }
    safePins.forEach((pin) => {
      const y = pin.indicacao?.ano || pin.ano;
      if (Number(y)) yearSet.add(Number(y));
    });
    if (yearSet.size === 0) {
      return [2021, 2022, 2023, 2024, 2025, 2026];
    }
    return Array.from(yearSet).sort((a, b) => a - b);
  }, [safeStats.anos, safeStats.demandas_por_ano, safePins]);

  // Contexto do Vereador Selecionado
  const { authorThemes, authorBairros, authorYears, authorCount } = useMemo(() => {
    if (!filters.autor) return { authorThemes: [], authorBairros: [], authorYears: [], authorCount: 0 };

    const authorPins = safePins.filter((p) => {
      const pinAuthor = p.indicacao?.autor || p.autor;
      return pinAuthor === filters.autor;
    });

    const catCounts = {};
    THEMATIC_CATEGORIES_ORDER.forEach((k) => {
      catCounts[k] = 0;
    });

    if (
      Array.isArray(safeStats.demandas_por_thematic_category) &&
      safeStats.demandas_por_thematic_category.length > 0
    ) {
      safeStats.demandas_por_thematic_category.forEach((item) => {
        if (item?.category) {
          catCounts[item.category] = Number(item.count) || 0;
        }
      });
    } else {
      authorPins.forEach((p) => {
        const catKey = p.indicacao?.thematic_category || p.thematic_category || "OUTROS";
        catCounts[catKey] = (catCounts[catKey] || 0) + 1;
      });
    }

    const bairroMap = new Map();
    authorPins.forEach((p) => {
      const raw = p.indicacao?.bairro || p.bairro;
      if (raw && raw !== "Não informado" && raw.trim()) {
        const b = canonicalizeBairro(raw);
        if (b) {
          bairroMap.set(b.toLowerCase(), b);
        }
      }
    });

    const total =
      safeStats.total_indicacoes !== undefined
        ? safeStats.total_indicacoes
        : authorPins.length;

    const sorted = THEMATIC_CATEGORIES_ORDER.map((catKey) => {
      const cat = THEMATIC_CATEGORIES[catKey] || {
        label: catKey,
        solid: "var(--accent-primary)",
        bgColor: "var(--accent-primary)",
        onSolid: "#ffffff",
        icon: "",
      };
      const count = catCounts[catKey] || 0;
      return {
        key: catKey,
        label: cat.label,
        color: cat.solid || cat.bgColor,
        onSolid: cat.onSolid || "#ffffff",
        icon: cat.icon || "",
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      };
    }).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label, "pt-BR");
    });

    // Distribuição por ano para o gráfico de área
    const yearCounts = {};
    availableYears.forEach((y) => {
      yearCounts[y] = 0;
    });

    if (Array.isArray(safeStats.demandas_por_ano) && safeStats.demandas_por_ano.length > 0) {
      safeStats.demandas_por_ano.forEach((item) => {
        if (item?.ano !== undefined && item?.ano !== null) {
          yearCounts[Number(item.ano)] = Number(item.count) || 0;
        }
      });
    } else {
      authorPins.forEach((p) => {
        const y = p.indicacao?.ano || p.ano;
        if (y !== undefined && y !== null) {
          yearCounts[y] = (yearCounts[y] || 0) + 1;
        }
      });
    }

    const authorYears = availableYears.map((year) => ({
      year,
      count: yearCounts[year] || 0,
    }));

    return {
      authorThemes: sorted,
      authorBairros: Array.from(bairroMap.values()).slice(0, 8),
      authorYears,
      authorCount: total,
    };
  }, [filters.autor, safeStats, safePins, availableYears]);

  // Nome e estilo da categoria ativa atualmente pelo filtro
  const activeCategoryName = useMemo(() => {
    if (filters.thematic_category) {
      return getCategoryLabel(filters.thematic_category);
    }
    return null;
  }, [filters.thematic_category]);

  const activeCategoryStyle = useMemo(() => {
    if (filters.thematic_category) {
      return THEMATIC_CATEGORIES[filters.thematic_category] || getCategoryStyle(filters.thematic_category);
    }
    return null;
  }, [filters.thematic_category]);

  // Contexto da Categoria Selecionada (para o painel direito)
  const categoryContextData = useMemo(() => {
    if (!filters.thematic_category) {
      return { categoryCount: 0, categoryYears: [], categoryAuthors: [], categoryBairros: [] };
    }

    const catPins = safePins.filter((p) => {
      const c = p.indicacao?.thematic_category || p.thematic_category;
      return c === filters.thematic_category;
    });

    const total =
      safeStats.total_indicacoes !== undefined
        ? safeStats.total_indicacoes
        : catPins.length;

    const yearCounts = {};
    availableYears.forEach((y) => {
      yearCounts[y] = 0;
    });

    if (Array.isArray(safeStats.demandas_por_ano) && safeStats.demandas_por_ano.length > 0) {
      safeStats.demandas_por_ano.forEach((item) => {
        if (item?.ano !== undefined && item?.ano !== null) {
          yearCounts[Number(item.ano)] = Number(item.count) || 0;
        }
      });
    } else {
      catPins.forEach((p) => {
        const y = p.indicacao?.ano || p.ano;
        if (y !== undefined && y !== null) {
          yearCounts[y] = (yearCounts[y] || 0) + 1;
        }
      });
    }
    const categoryYears = availableYears.map((year) => ({
      year,
      count: yearCounts[year] || 0,
    }));

    const authorCounts = {};

    if (Array.isArray(safeStats.demandas_por_autor) && safeStats.demandas_por_autor.length > 0) {
      safeStats.demandas_por_autor.forEach((item) => {
        if (item?.autor) {
          authorCounts[item.autor] = Number(item.count) || 0;
        }
      });
    } else {
      catPins.forEach((p) => {
        const authorStr = p.indicacao?.autor || p.autor;
        if (authorStr) {
          authorStr
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
            .forEach((a) => {
              authorCounts[a] = (authorCounts[a] || 0) + 1;
            });
        }
      });
    }

    const catBairroMap = new Map();
    catPins.forEach((p) => {
      const raw = p.indicacao?.bairro || p.bairro;
      if (raw && raw !== "Não informado" && raw.trim()) {
        const b = canonicalizeBairro(raw);
        if (b) {
          catBairroMap.set(b.toLowerCase(), b);
        }
      }
    });

    const maxAuthor = Math.max(...Object.values(authorCounts), 1);
    const categoryAuthors = availableAuthors
      .map((author) => {
        const count = authorCounts[author] || 0;
        return {
          author,
          count,
          percentOfTotal: total > 0 ? Math.round((count / total) * 100) : 0,
          percentage: count === 0 ? 0 : (count / maxAuthor) * 100,
        };
      })
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.author.localeCompare(b.author, "pt-BR");
      });

    return {
      categoryCount: total,
      categoryYears,
      categoryAuthors,
      categoryBairros: Array.from(catBairroMap.values()).slice(0, 8),
    };
  }, [filters.thematic_category, safeStats, safePins, availableYears, availableAuthors]);

  // Verificação de filtro por ano ativo
  const isYearFiltered = Array.isArray(filters.anos) && filters.anos.length > 0;
  const activeYearLabel = useMemo(() => {
    if (filters.anos && filters.anos.length === 1) return String(filters.anos[0]);
    if (filters.anos && filters.anos.length > 1) return filters.anos.join(", ");
    return null;
  }, [filters.anos]);

  // Distribuição Cronológica Global
  const globalDistributionYears = useMemo(() => {
    const yearCounts = {};
    availableYears.forEach((y) => {
      yearCounts[y] = 0;
    });

    if (Array.isArray(safeStats.demandas_por_ano) && safeStats.demandas_por_ano.length > 0) {
      safeStats.demandas_por_ano.forEach((item) => {
        if (item?.ano !== undefined && item?.ano !== null) {
          yearCounts[Number(item.ano)] = Number(item.count) || 0;
        }
      });
    } else {
      safePins.forEach((p) => {
        const y = p.indicacao?.ano || p.ano;
        if (y !== undefined && y !== null) {
          yearCounts[y] = (yearCounts[y] || 0) + 1;
        }
      });
    }

    return availableYears.map((year) => ({
      year,
      count: yearCounts[year] || 0,
    }));
  }, [safeStats.demandas_por_ano, safePins, availableYears]);

  // Contagens por categoria temática com percentuais relativos
  const categoryStats = useMemo(() => {
    const counts = {};
    THEMATIC_CATEGORIES_ORDER.forEach((catKey) => {
      counts[catKey] = 0;
    });

    if (
      Array.isArray(safeStats.demandas_por_thematic_category) &&
      safeStats.demandas_por_thematic_category.length > 0
    ) {
      safeStats.demandas_por_thematic_category.forEach((item) => {
        if (item?.category) {
          counts[item.category] = Number(item.count) || 0;
        }
      });
    } else {
      safePins.forEach((pin) => {
        const catKey = pin.indicacao?.thematic_category || "OUTROS";
        counts[catKey] = (counts[catKey] || 0) + 1;
      });
    }

    const totalCount = Object.values(counts).reduce((sum, v) => sum + v, 0) || 1;
    const maxCount = Math.max(...Object.values(counts), 1);

    return THEMATIC_CATEGORIES_ORDER.map((catKey) => {
      const cat = THEMATIC_CATEGORIES[catKey] || {};
      const c = counts[catKey] || 0;
      return {
        key: catKey,
        name: cat.label || catKey,
        label: cat.label || catKey,
        color: cat.solid || cat.bgColor || "var(--accent-primary)",
        onSolid: cat.onSolid || "#ffffff",
        icon: cat.icon || "",
        style: cat,
        count: c,
        percentOfTotal: totalCount > 0 ? Math.round((c / totalCount) * 100) : 0,
        percentage: (c / maxCount) * 100,
        barWidth: (c / maxCount) * 100,
      };
    });
  }, [safeStats.demandas_por_thematic_category, safePins]);

  // Contagens por Ano
  const yearStats = useMemo(() => {
    const counts = {};

    if (Array.isArray(safeStats.demandas_por_ano) && safeStats.demandas_por_ano.length > 0) {
      safeStats.demandas_por_ano.forEach((item) => {
        if (item?.ano !== undefined && item?.ano !== null) {
          counts[Number(item.ano)] = Number(item.count) || 0;
        }
      });
    } else {
      safePins.forEach((pin) => {
        const ano = pin.indicacao?.ano;
        if (ano) {
          counts[ano] = (counts[ano] || 0) + 1;
        }
      });
    }

    const maxCount = Math.max(...Object.values(counts), 1);
    const sortedYears = Object.keys(counts).sort((a, b) => b - a);

    return sortedYears.map((year) => ({
      year: parseInt(year, 10),
      count: counts[year] || 0,
      percentage: ((counts[year] || 0) / maxCount) * 100,
    }));
  }, [safeStats.demandas_por_ano, safePins]);

  // Contagens por Autor (ranking ordenado)
  const authorStats = useMemo(() => {
    const counts = {};

    if (Array.isArray(safeStats.demandas_por_autor) && safeStats.demandas_por_autor.length > 0) {
      safeStats.demandas_por_autor.forEach((item) => {
        if (item?.autor) {
          counts[item.autor] = Number(item.count) || 0;
        }
      });
    } else {
      safePins.forEach((pin) => {
        const authorStr = pin.indicacao?.autor;
        if (authorStr) {
          authorStr
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
            .forEach((author) => {
              counts[author] = (counts[author] || 0) + 1;
            });
        }
      });
    }

    const maxAuthor = Math.max(...Object.values(counts), 1);
    return availableAuthors
      .map((author) => {
        const count = counts[author] || 0;
        return {
          author,
          count,
          percentage: count === 0 ? 0 : (count / maxAuthor) * 100,
        };
      })
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.author.localeCompare(b.author, "pt-BR");
      })
      .map((item, idx) => ({
        ...item,
        rank: idx + 1,
      }));
  }, [availableAuthors, safeStats.demandas_por_autor, safePins]);

  const totalBase =
    safeStats.total_indicacoes !== undefined
      ? safeStats.total_indicacoes
      : (indicacoesTotal || safePins.length || 1);

  // Categorias ordenadas por volume de demandas
  const sortedCategories = useMemo(() => {
    return [...categoryStats].sort((a, b) => b.count - a.count);
  }, [categoryStats]);

  const globalAuthorBars = useMemo(() => {
    const total = totalBase || 1;
    return authorStats.map((item) => ({
      ...item,
      percentOfTotal: Math.round((item.count / total) * 100),
    }));
  }, [authorStats, totalBase]);

  // Identificação da categoria líder ou ativa para o card narrativo
  const currentCategoryKey = filters.thematic_category;
  const topCategory = useMemo(() => {
    if (currentCategoryKey) {
      return categoryStats.find((c) => c.key === currentCategoryKey) || sortedCategories[0];
    }
    return sortedCategories[0];
  }, [currentCategoryKey, categoryStats, sortedCategories]);

  // Função geradora de sínteses narrativas cívicas por tema
  const getCategoryNarrative = (catKey) => {
    switch (catKey) {
      case "INFRAESTRUTURA_VIARIA":
        return "Manutenção de asfalto, calçamento e reparos em vias lideram as solicitações urbanas da cidade.";
      case "TRANSITO_SINALIZACAO":
        return "Demandas por sinalização, redutores de velocidade e faixas de pedestres concentram atenção.";
      case "ILUMINACAO_PUBLICA":
        return "Pedidos frequentes para modernização de luminárias, extensão de rede e reforço noturno.";
      case "LIMPEZA_ZELADORIA":
        return "Serviços essenciais de roçagem, desobstrução de galerias e limpeza de áreas públicas.";
      case "SAUDE":
        return "Reivindicações de atendimento em UBSs, farmácia municipal, exames e especialidades.";
      case "EDUCACAO":
        return "Melhorias estruturais em creches e unidades escolares municipais de Artur Nogueira.";
      case "ESPORTE_LAZER":
        return "Revitalização de praças, quadras esportivas e espaços de convivência comunitária.";
      case "SANEAMENTO_AGUA":
        return "Obras de drenagem pluvial, abastecimento e saneamento básico nos bairros.";
      case "SEGURANCA":
        return "Reforço de vigilância e rondas da Guarda Municipal em corredores de circulação.";
      default:
        return "Indicações de serviços públicos e zeladoria protocoladas na Câmara Municipal.";
    }
  };


  const handleYearClick = (yearNum) => {
    let currentAnos = Array.isArray(filters.anos) ? [...filters.anos] : [];
    if (currentAnos.includes(yearNum)) {
      currentAnos = currentAnos.filter((y) => y !== yearNum);
    } else {
      currentAnos.push(yearNum);
    }
    applyFilters({
      anos: currentAnos,
    });
  };

  // Resumo para o Modal
  const modalSummary = useMemo(() => {
    const topCat = sortedCategories[0];
    const topAut = authorStats[0];
    return {
      total: totalBase,
      totalAuthors: authorStats.length,
      topCategory: topCat ? `${topCat.name} (${topCat.count})` : "N/D",
      topAuthor: topAut ? `${topAut.author} (${topAut.count})` : "N/D",
    };
  }, [totalBase, sortedCategories, authorStats]);

  if (isCollapsed) {
    if (isMobile) return null;

    return (
      <button
        type="button"
        className="glass-card"
        onClick={onToggleCollapse}
        style={{
          position: "absolute",
          right: 14,
          top: topOffset,
          zIndex: 1040,
          height: 38,
          padding: "0 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--radius-md)",
          color: "var(--text-primary)",
          fontWeight: 600,
          fontSize: "var(--text-label)",
          boxShadow: "var(--shadow-md)",
          background: "var(--bg-glass)",
          transition: "all var(--transition-fast)",
          maxWidth: 280,
          boxSizing: "border-box",
        }}
        title="Abrir Visão Geral"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-chart-pie"
          style={{ color: "var(--accent-primary)", flexShrink: 0 }}
        >
          <path d="M21 12c.552 0 1.005-.449.95-.998a10 10 0 0 0-8.953-8.951c-.55-.055-.998.398-.998.95v8a1 1 0 0 0 1 1z" />
          <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
        </svg>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Visão Geral
        </span>
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "var(--text-muted)", flexShrink: 0, marginLeft: 1 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
    );
  }

  return (
    <>
      <div
        className="glass-card"
        onWheel={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          left: isMobile ? 12 : "auto",
          right: isMobile ? 12 : 14,
          top: topOffset,
          bottom: isMobile ? 68 : 14,
          zIndex: 1040,
          width: isMobile ? "auto" : 320,
          height: isMobile ? "calc(100% - 82px)" : "calc(100% - 28px)",
          maxHeight: isMobile ? "calc(100% - 82px)" : "calc(100% - 28px)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-glass)",
          border: "1px solid var(--border-color)",
          overflow: "hidden",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        }}
      >
        {/* ── Cabeçalho Dinâmico e Contextual ── */}
        {/* ── Cabeçalho do Painel: Sempre 'Visão Geral' com ícone lucide-chart-pie ── */}
        <div
          onClick={onToggleCollapse}
          style={{
            height: 46,
            minHeight: 46,
            maxHeight: 46,
            padding: "0 16px",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            userSelect: "none",
            background: "var(--bg-glass)",
            cursor: "pointer",
            boxSizing: "border-box",
            flexShrink: 0,
            transition: "background var(--transition-fast)",
          }}
          title="Clique para recolher"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-chart-pie"
              style={{ color: "var(--accent-primary)", flexShrink: 0 }}
            >
              <path d="M21 12c.552 0 1.005-.449.95-.998a10 10 0 0 0-8.953-8.951c-.55-.055-.998.398-.998.95v8a1 1 0 0 0 1 1z" />
              <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
            </svg>
            <h3
              style={{
                margin: 0,
                fontSize: "var(--text-body-sm)",
                fontWeight: 700,
                color: "var(--text-primary)",
                letterSpacing: "var(--tracking-tight)",
              }}
            >
              Visão Geral
            </h3>
          </div>

          {/* Ações à Direita do Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  resetFilters();
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--accent-primary)",
                  fontSize: "var(--text-label)",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "2px 4px",
                }}
                title="Limpar todos os filtros"
              >
                Limpar
              </button>
            ) : null}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
              style={{
                background: "transparent",
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: "var(--text-muted)",
                fontSize: "var(--text-label)",
                fontWeight: 500,
                cursor: "pointer",
                padding: "2px 4px",
              }}
              title="Recolher painel"
            >
              <span>Recolher</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── CORPO DO PAINEL UNIFICADO COM SCROLL (3 ESTADOS CONTEXTUAIS) ── */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "14px 16px",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {filters.autor ? (
            /* ===================================================================
               ESTADO 1: VEREADOR SELECIONADO
               =================================================================== */
            <>
              <div>
                <div style={{ fontSize: "var(--text-micro)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--accent-primary)", marginBottom: 4 }}>
                  Vereador Selecionado
                </div>
                <h3 style={{ margin: "0 0 4px", fontSize: "var(--text-title-sm)", fontWeight: 700, color: "var(--text-primary)" }}>
                  {filters.autor}
                </h3>
                <div style={{ fontSize: "var(--text-label)", color: "var(--text-muted)" }}>
                  Distribuição das indicações do mandato
                </div>
              </div>

              {/* Volume de Indicações no Período (Anti-card: sem fundo cinza nem borda) */}
              <div>
                <div style={{ fontSize: "var(--text-display)", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "var(--text-primary)", lineHeight: 1 }}>
                  {authorCount}
                </div>
                <div style={{ fontSize: "var(--text-body-sm)", fontWeight: 600, color: "var(--text-secondary)", marginTop: 4 }}>
                  indicações {activeYearLabel ? `em ${activeYearLabel}` : "no período selecionado"}
                </div>
                <div style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)", marginTop: 4, lineHeight: "var(--leading-snug)" }}>
                  Dado descritivo das proposições de zeladoria e melhorias urbanas protocoladas na Câmara Municipal{availableYears.length > 0 ? ` (${availableYears[0]}–${availableYears[availableYears.length - 1]})` : ""}.
                </div>
              </div>

              {/* Gráfico de Área: Distribuição por Ano no topo (oculto quando filtrado por ano) */}
              {!isYearFiltered && (
                <DistributionAreaChart
                  yearData={authorYears}
                  selectedYears={filters.anos || []}
                  onSelectYear={handleYearClick}
                />
              )}

              {/* Distribuição por Categoria Temática: Temas Abordados */}
              <div>
                <div
                  style={{
                    fontSize: "var(--text-micro)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-wide)",
                    color: "var(--text-muted)",
                    marginBottom: 10,
                  }}
                >
                  Temas Abordados
                </div>

                <CategoryBarsList
                  categories={authorThemes}
                  activeCategory={filters.thematic_category}
                  onSelectCategory={(catKey) =>
                    applyFilters({
                      thematic_category: filters.thematic_category === catKey ? null : catKey,
                    })
                  }
                />
              </div>

              {/* Bairros de Atuação se houver */}
              {authorBairros.length > 0 && (
                <div>
                  <div style={{ fontSize: "var(--text-micro)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--text-muted)", marginBottom: 6 }}>
                    Bairros Atendidos
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {authorBairros.map((b) => {
                      const isActive =
                        filters.bairro &&
                        (filters.bairro.toLowerCase() === b.toLowerCase() ||
                          areSameNeighborhood(filters.bairro, b));
                      return (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setBairro(isActive ? null : b)}
                          title={isActive ? "Remover filtro deste bairro" : `Filtrar por ${b}`}
                          style={{
                            fontSize: "var(--text-micro)",
                            padding: "3px 8px",
                            borderRadius: "var(--radius-sm)",
                            background: isActive ? "var(--accent-light)" : "var(--bg-surface-elevated)",
                            border: `1px solid ${isActive ? "var(--accent-primary)" : "var(--border-subtle)"}`,
                            color: isActive ? "var(--accent-primary)" : "var(--text-secondary)",
                            fontWeight: isActive ? 700 : 500,
                            cursor: "pointer",
                            transition: "all var(--transition-fast)",
                          }}
                        >
                          {b}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Ação para limpar filtro de vereador */}
              <div style={{ paddingTop: 4 }}>
                <button
                  type="button"
                  onClick={() => setAutor(null)}
                  style={{
                    width: "100%",
                    padding: "7px 12px",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-surface)",
                    color: "var(--text-secondary)",
                    fontWeight: 600,
                    fontSize: "var(--text-label)",
                    cursor: "pointer",
                    transition: "all var(--transition-fast)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
                >
                  Limpar filtro de vereador
                </button>
              </div>
            </>
          ) : filters.thematic_category ? (
            /* ===================================================================
               ESTADO 2: CATEGORIA SELECIONADA
               =================================================================== */
            <>
              <div>
                <div style={{ fontSize: "var(--text-micro)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--accent-primary)", marginBottom: 4 }}>
                  Tema Selecionado
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 4px" }}>
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "var(--radius-sm)",
                      backgroundColor: activeCategoryStyle?.solid || activeCategoryStyle?.bgColor || "var(--accent-primary)",
                      color: activeCategoryStyle?.onSolid || "#ffffff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                    }}
                  >
                    <span
                      className="geom-svg-icon"
                      style={{
                        width: 13,
                        height: 13,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "inherit",
                      }}
                      dangerouslySetInnerHTML={{ __html: activeCategoryStyle?.icon || "" }}
                    />
                  </div>
                  <h3 style={{ margin: 0, fontSize: "var(--text-title-sm)", fontWeight: 700, color: "var(--text-primary)" }}>
                    {activeCategoryName}
                  </h3>
                </div>
                <div style={{ fontSize: "var(--text-label)", color: "var(--text-muted)", lineHeight: "var(--leading-snug)" }}>
                  {getCategoryNarrative(filters.thematic_category)}
                </div>
              </div>

              {/* Volume de Indicações no Tema (Anti-card: sem fundo cinza) */}
              <div>
                <div style={{ fontSize: "var(--text-display)", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "var(--text-primary)", lineHeight: 1 }}>
                  {categoryContextData.categoryCount}
                </div>
                <div style={{ fontSize: "var(--text-body-sm)", fontWeight: 600, color: "var(--text-secondary)", marginTop: 4 }}>
                  indicações {activeYearLabel ? `em ${activeYearLabel}` : "no período selecionado"}
                </div>
                <div style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)", marginTop: 4, lineHeight: "var(--leading-snug)" }}>
                  Proposições deste tema protocoladas na Câmara Municipal{availableYears.length > 0 ? ` (${availableYears[0]}–${availableYears[availableYears.length - 1]})` : ""}.
                </div>
              </div>

              {/* Gráfico de Área: Distribuição Anual do Tema (oculto quando filtrado por ano) */}
              {!isYearFiltered && (
                <DistributionAreaChart
                  yearData={categoryContextData.categoryYears}
                  selectedYears={filters.anos || []}
                  onSelectYear={handleYearClick}
                />
              )}

              {/* Distribuição por Parlamentar no Tema */}
              <div>
                <div
                  style={{
                    fontSize: "var(--text-micro)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-wide)",
                    color: "var(--text-muted)",
                    marginBottom: 10,
                  }}
                >
                  Vereadores
                </div>

                <CouncilorBarsList
                  authors={categoryContextData.categoryAuthors}
                  activeAuthor={filters.autor}
                  onSelectAuthor={(author) => setAutor(author)}
                />
              </div>

              {/* Bairros de Atuação no Tema */}
              {categoryContextData.categoryBairros.length > 0 && (
                <div>
                  <div style={{ fontSize: "var(--text-micro)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--text-muted)", marginBottom: 6 }}>
                    Bairros Atendidos
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {categoryContextData.categoryBairros.map((b) => {
                      const isActive =
                        filters.bairro &&
                        (filters.bairro.toLowerCase() === b.toLowerCase() ||
                          areSameNeighborhood(filters.bairro, b));
                      return (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setBairro(isActive ? null : b)}
                          title={isActive ? "Remover filtro deste bairro" : `Filtrar por ${b}`}
                          style={{
                            fontSize: "var(--text-micro)",
                            padding: "3px 8px",
                            borderRadius: "var(--radius-sm)",
                            background: isActive ? "var(--accent-light)" : "var(--bg-surface-elevated)",
                            border: `1px solid ${isActive ? "var(--accent-primary)" : "var(--border-subtle)"}`,
                            color: isActive ? "var(--accent-primary)" : "var(--text-secondary)",
                            fontWeight: isActive ? 700 : 500,
                            cursor: "pointer",
                            transition: "all var(--transition-fast)",
                          }}
                        >
                          {b}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Ação para limpar filtro de tema */}
              <div style={{ paddingTop: 4 }}>
                <button
                  type="button"
                  onClick={() => applyFilters({ thematic_category: null })}
                  style={{
                    width: "100%",
                    padding: "7px 12px",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-surface)",
                    color: "var(--text-secondary)",
                    fontWeight: 600,
                    fontSize: "var(--text-label)",
                    cursor: "pointer",
                    transition: "all var(--transition-fast)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
                >
                  Limpar filtro de tema
                </button>
              </div>
            </>
          ) : (
            /* ===================================================================
               ESTADO 3: PANORAMA GERAL MUNICIPAL (SEM FILTROS ESPECÍFICOS)
               =================================================================== */
            <>
              <div>
                <div style={{ fontSize: "var(--text-micro)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "var(--tracking-wide)", color: "var(--accent-primary)", marginBottom: 4 }}>
                  Panorama Municipal
                </div>
                <h3 style={{ margin: "0 0 4px", fontSize: "var(--text-title-sm)", fontWeight: 700, color: "var(--text-primary)" }}>
                  Câmara de Artur Nogueira
                </h3>
                <div style={{ fontSize: "var(--text-label)", color: "var(--text-muted)" }}>
                  Mapeamento geográfico de proposições legislativas
                </div>
              </div>

              {/* Volume Global de Indicações (Anti-card: sem fundo cinza) */}
              <div>
                <div style={{ fontSize: "var(--text-display)", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: "var(--text-primary)", lineHeight: 1 }}>
                  {totalBase}
                </div>
                <div style={{ fontSize: "var(--text-body-sm)", fontWeight: 600, color: "var(--text-secondary)", marginTop: 4 }}>
                  indicações {activeYearLabel ? `em ${activeYearLabel}` : "no período selecionado"}
                </div>
                <div style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)", marginTop: 4, lineHeight: "var(--leading-snug)" }}>
                  Total de proposições de melhorias urbanas e zeladoria protocoladas no período legislativo.
                </div>
              </div>

              {/* Gráfico de Área: Distribuição Cronológica Geral (oculto quando filtrado por ano) */}
              {!isYearFiltered && (
                <DistributionAreaChart
                  yearData={globalDistributionYears}
                  selectedYears={filters.anos || []}
                  onSelectYear={handleYearClick}
                />
              )}

              {/* Temas Abordados em Toda a Cidade */}
              <div>
                <div
                  style={{
                    fontSize: "var(--text-micro)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-wide)",
                    color: "var(--text-muted)",
                    marginBottom: 10,
                  }}
                >
                  Temas Abordados
                </div>

                <CategoryBarsList
                  categories={sortedCategories}
                  activeCategory={filters.thematic_category}
                  onSelectCategory={(catKey) =>
                    applyFilters({
                      thematic_category: filters.thematic_category === catKey ? null : catKey,
                    })
                  }
                />
              </div>

              {/* Distribuição por Parlamentar em Toda a Cidade */}
              <div>
                <div
                  style={{
                    fontSize: "var(--text-micro)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-wide)",
                    color: "var(--text-muted)",
                    marginBottom: 10,
                  }}
                >
                  Vereadores
                </div>

                <CouncilorBarsList
                  authors={globalAuthorBars}
                  activeAuthor={filters.autor}
                  onSelectAuthor={(author) => setAutor(author)}
                />
              </div>
            </>
          )}

          {/* Botão de Rodapé para Relatório Analítico Completo */}
          <div style={{ paddingTop: 8, borderTop: "1px solid var(--border-subtle)" }}>
            <button
              type="button"
              onClick={() => {
                if (navigate) {
                  navigate("/estatisticas");
                } else {
                  setShowModal(true);
                }
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "8px 12px",
                background: "var(--bg-app)",
                border: "1px solid var(--border-color)",
                borderRadius: "var(--radius-md)",
                color: "var(--text-primary)",
                fontSize: "var(--text-label)",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all var(--transition-fast)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--accent-primary)";
                e.currentTarget.style.color = "var(--accent-primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--border-color)";
                e.currentTarget.style.color = "var(--text-primary)";
              }}
              title="Abrir página completa de estatísticas municipais"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-chart-no-axes-combined"
                style={{ flexShrink: 0 }}
              >
                <path d="M12 16v5" />
                <path d="M16 14.639V21" />
                <path d="M20 10.656V21" />
                <path d="m22 3-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.707 0L2 15" />
                <path d="M4 18.463V21" />
                <path d="M8 14.656V21" />
              </svg>
              <span>Ver Estatísticas Completas</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, color: "var(--text-muted)" }}
              >
                <path d="M7 17L17 7" />
                <polyline points="7 7 17 7 17 17" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Modal Informativo com Síntese Executiva */}
      <AnalyticsModal
        isOpen={showModal}
        summary={modalSummary}
        onClose={() => setShowModal(false)}
      />
    </>
  );
}
